// Derived cohort metrics.
//
// Pure functions over the /admin/learner-signals payload. Kept out of the page
// component so the numbers behind a decision can be read (and corrected)
// without scrolling through JSX.
//
// The organising idea: a raw count is rarely a decision. "29 lessons completed"
// does not tell an admin what to do. "Completed 93% of what they started, 18
// points above the cohort median" does. Everything here turns counts into
// rates, gaps against a reference, and named states.

export const learnerName = (row) => {
  const user = row?.user || {};
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Learner';
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const median = (values) => {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
};

// A learner who has started nothing has no completion rate. That is different
// from a rate of 0% (started ten, finished none), and the charts must not
// draw them at the same place — the old dashboard did, which is how a learner
// with no activity and a learner who stalled mid-way looked identical.
export const completionRate = (row) => {
  const started = num(row?.lessons_started);
  if (!started) return null;
  return Math.round((num(row?.lessons_completed) / started) * 100);
};

export const takeHomeScores = (row, practiceKeys) => (
  practiceKeys
    .map((key) => row?.[key])
    .filter((state) => state?.grade_summary)
    .map((state) => num(state.grade_summary.percent))
);

export const avgTakeHome = (row, practiceKeys) => {
  const scores = takeHomeScores(row, practiceKeys);
  if (!scores.length) return null;
  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
};

export const understandingScore = (row, field = 'dcc_understanding') => {
  const latest = row?.[field]?.latest;
  if (!latest || latest.percent == null) return null;
  return Math.round(num(latest.percent));
};

// The calibration gap is the single most decision-relevant number in the DCC
// data and the old dashboard never computed it. Positive means the learner
// rates their understanding above what their graded work demonstrates
// (overconfident — they will not ask for help). Negative means the opposite
// (underconfident — they are ready for harder work and do not know it).
export const calibrationGap = (understanding, takeHome) => (
  understanding == null || takeHome == null ? null : understanding - takeHome
);

export const CALIBRATION_BAND = 15;

export const calibrationState = (gap) => {
  if (gap == null) return { key: 'unknown', label: 'Not enough evidence', status: 'unknown' };
  if (gap > CALIBRATION_BAND) return { key: 'overconfident', label: 'Overconfident', status: 'critical' };
  if (gap < -CALIBRATION_BAND) return { key: 'underconfident', label: 'Underconfident', status: 'warning' };
  return { key: 'calibrated', label: 'Well calibrated', status: 'good' };
};

export const clinicRecordCount = (row) => (row?.concept_clinic_records || []).length;

export const evidenceCount = (row) => num(row?.journal_count) + clinicRecordCount(row);

// One row per learner, carrying every derived measure the charts need, so a
// chart never recomputes and two charts can never disagree about a learner.
export const buildLearnerMetrics = (rows = [], practiceKeys = []) => rows.map((row) => {
  const understanding = understandingScore(row);
  const takeHome = avgTakeHome(row, practiceKeys);
  const gap = calibrationGap(understanding, takeHome);
  return {
    id: row.user?.id,
    name: learnerName(row),
    row,
    status: row.status || 'active',
    band: row.engagement_band || 'cold',
    engagementScore: num(row.engagement_score),
    confidence: num(row.avg_confidence),
    lessonsStarted: num(row.lessons_started),
    lessonsCompleted: num(row.lessons_completed),
    completionRate: completionRate(row),
    journals: num(row.journal_count),
    clinicRecords: clinicRecordCount(row),
    evidence: evidenceCount(row),
    events7d: num(row.events_7d),
    understanding,
    takeHome,
    calibrationGap: gap,
    calibration: calibrationState(gap),
    signals: row.signals || [],
    recommendedAction: row.recommended_action || '',
    lastActivityAt: row.last_activity_at || null,
  };
});

export const cohortReference = (metrics = []) => ({
  medianCompletion: median(metrics.map((m) => m.completionRate).filter((v) => v != null)),
  medianConfidence: median(metrics.map((m) => m.confidence).filter((v) => v > 0)),
  medianEngagement: median(metrics.map((m) => m.engagementScore)),
  medianEvidence: median(metrics.map((m) => m.evidence)),
});

// Quadrant naming for the confidence-vs-delivery plot. Position carries the
// meaning; the label makes it explicit so the reader never has to infer which
// corner is bad.
export const deliveryQuadrant = (metric, reference) => {
  const rate = metric.completionRate;
  const confidence = metric.confidence;
  if (rate == null) return { key: 'not_started', label: 'Not started', status: 'unknown' };
  const highRate = rate >= reference.medianCompletion;
  const highConfidence = confidence >= reference.medianConfidence;
  if (highRate && highConfidence) return { key: 'thriving', label: 'On track', status: 'good' };
  if (highRate && !highConfidence) return { key: 'grinding', label: 'Delivering, low confidence', status: 'warning' };
  if (!highRate && highConfidence) return { key: 'coasting', label: 'Confident, not finishing', status: 'serious' };
  return { key: 'stalled', label: 'Stalled', status: 'critical' };
};

// Nested by construction: each stage is a strict subset of the one above it,
// so the funnel cannot render a stage wider than its parent. The old funnel
// mixed learner counts with take-home counts and drew stage 4 bigger than
// stage 1, which is what made it unreadable.
export const buildProgressFunnel = (metrics = []) => {
  const inCohort = metrics;
  const started = inCohort.filter((m) => m.lessonsStarted > 0);
  const completed = started.filter((m) => m.lessonsCompleted > 0);
  const evidenced = completed.filter((m) => m.evidence > 0);
  const strong = evidenced.filter((m) => m.band === 'strong');
  return [
    { key: 'in_cohort', label: 'In cohort', value: inCohort.length, members: inCohort },
    { key: 'started', label: 'Started a lesson', value: started.length, members: started },
    { key: 'completed', label: 'Completed a lesson', value: completed.length, members: completed },
    { key: 'evidenced', label: 'Saved evidence', value: evidenced.length, members: evidenced },
    { key: 'strong', label: 'Strong engagement', value: strong.length, members: strong },
  ];
};

// The biggest stage-to-stage drop is where the cohort is actually losing
// people. That is the intervention point, so the chart should name it rather
// than leaving the admin to eyeball five bars.
export const biggestFunnelDrop = (stages = []) => {
  let worst = null;
  for (let i = 1; i < stages.length; i += 1) {
    const lost = stages[i - 1].value - stages[i].value;
    if (lost > 0 && (!worst || lost > worst.lost)) {
      worst = { lost, from: stages[i - 1], to: stages[i] };
    }
  }
  return worst;
};

const LENS_STATUS_SCORE = { correct: 1, partial: 0.5, missed: 0, ungraded: null };

const lensStatusFor = (grade, lensKey, lensLabel) => {
  const result = grade?.lensResults?.[lensKey];
  if (result?.status) return result.status;
  if ((grade?.strengths || []).includes(lensLabel)) return 'correct';
  if ((grade?.missing || []).includes(lensLabel)) return 'missed';
  return 'ungraded';
};

// Rolls the wall of per-table rubric chips into a learner x lens matrix.
// Answers "what do I reteach on Monday?" — a question the chip wall cannot
// answer at all without reading several hundred chips one at a time.
export const buildLensMatrix = (rows = [], config) => {
  if (!config) return { lenses: [], learners: [], cohort: [] };
  const lenses = config.lenses.map(([key, label]) => ({ key, label }));

  const learners = rows.map((row) => {
    const practiceState = row?.[config.practiceKey] || {};
    const grades = practiceState.grades || {};
    const tables = Object.entries(grades).filter(([, grade]) => grade);
    const cells = lenses.map((lens) => {
      const scores = tables
        .map(([, grade]) => LENS_STATUS_SCORE[lensStatusFor(grade, lens.key, lens.label)])
        .filter((score) => score != null);
      return {
        lensKey: lens.key,
        lensLabel: lens.label,
        graded: scores.length,
        ratio: scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null,
      };
    });
    return { id: row.user?.id, name: learnerName(row), tables: tables.length, cells };
  });

  const cohort = lenses.map((lens) => {
    const ratios = learners
      .map((learner) => learner.cells.find((cell) => cell.lensKey === lens.key)?.ratio)
      .filter((ratio) => ratio != null);
    return {
      ...lens,
      ratio: ratios.length ? ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length : null,
      covered: ratios.length,
    };
  });

  return { lenses, learners, cohort };
};

// Per-lens rubric profile for one learner against the cohort average.
// The understanding reviews already store a per-lens breakdown; the previous
// dashboard only ever showed the rolled-up percentage, so "62%" gave no clue
// which part of the explanation was weak.
export const buildRubricAxes = (rows = [], field, lensTemplates = [], focusRow = null) => {
  const reviewed = rows.filter((row) => row?.[field]?.latest?.lenses);
  const focusLenses = focusRow?.[field]?.latest?.lenses;
  if (!reviewed.length || !focusLenses) return [];

  return lensTemplates.map((template) => {
    const cohortScores = reviewed
      .map((row) => Number(row[field].latest.lenses?.[template.key]?.score))
      .filter((score) => Number.isFinite(score));
    const learnerScore = Number(focusLenses[template.key]?.score);
    return {
      key: template.key,
      label: template.label,
      learner: Number.isFinite(learnerScore) ? Math.round(learnerScore * 100) : 0,
      cohort: cohortScores.length
        ? Math.round((cohortScores.reduce((sum, score) => sum + score, 0) / cohortScores.length) * 100)
        : 0,
    };
  });
};

export const weakestLenses = (cohort = [], count = 3) => (
  cohort
    .filter((lens) => lens.ratio != null)
    .sort((a, b) => a.ratio - b.ratio)
    .slice(0, count)
);

// Priority ordering for the action queue. The backend already computes a
// recommended action per learner and the previous UI dropped it entirely;
// this puts it on screen, worst first.
const STATUS_RANK = { at_risk: 0, waiting_setup: 1, active: 2, high_signal: 3 };

export const buildActionQueue = (metrics = []) => (
  metrics
    .filter((m) => m.status === 'at_risk' || m.status === 'waiting_setup' || m.calibration.key === 'overconfident')
    .sort((a, b) => {
      const rank = (STATUS_RANK[a.status] ?? 4) - (STATUS_RANK[b.status] ?? 4);
      if (rank !== 0) return rank;
      return a.engagementScore - b.engagementScore;
    })
);

export const formatRelativeDays = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'unknown';
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};
